/**
 * EndOfList — the product's ONE way of saying "there is no more".
 *
 * Four surfaces used to say it four ways: the grid printed
 * `End of list — 912 inquiries`, Settings → Team the same sentence with a
 * different noun, the Tasks queue said `That is the whole queue`, and a fully
 * loaded board column said nothing at all. Same situation, four sentences.
 *
 * The sentence is now built here and nowhere else:
 *
 *     End of list — 1,204 tasks
 *     End of list — 1 inquiry
 *
 * The number is what the SERVER says matches — the same figure `CountLabel`
 * prints in the toolbar — never `rows.length`, so the foot of the list and the
 * head of the page cannot disagree. Grouped digits, `.tnum`, never compacted, on
 * the same rules as every other count in the product.
 *
 * `align` is the one thing a surface chooses. The grid centres it under a
 * centred table; the Tasks queue keeps it on the row grid's left vertical, where
 * every other object on that screen starts. The WORDS never move.
 */

import { cn } from '@/lib/cn';
import { compactNumber, pluralise } from '@/lib/format';

export interface EndOfListProps {
  /** Rows the current query matches, per the server. */
  total: number;
  /** Singular noun: `inquiry`, `task`, `person`. */
  noun: string;
  /** Irregular plural. Defaults to `noun + s`. */
  nounPlural?: string;
  align?: 'center' | 'start';
  className?: string;
}

/** The sentence itself, for anywhere that needs the string rather than the node. */
export function endOfListLabel(total: number, noun: string, nounPlural?: string): string {
  const word = pluralise(total, noun, nounPlural, { includeCount: false });
  return `End of list — ${compactNumber(total, { compact: false })} ${word}`;
}

export function EndOfList({
  total,
  noun,
  nounPlural,
  align = 'center',
  className,
}: EndOfListProps) {
  return (
    <div
      className={cn(
        'flex h-[var(--h-row-compact)] items-center text-xs text-muted tnum',
        align === 'center' ? 'justify-center' : 'justify-start',
        className,
      )}
    >
      <span aria-live="polite">{endOfListLabel(total, noun, nounPlural)}</span>
    </div>
  );
}
